<script setup lang="ts">
import { ref, onMounted } from "vue";
import MainLayout from "@/layouts/MainLayout.vue";
import BonusReportDialog from "@/components/installer/BonusReportDialog.vue";
import DeleteReportConfirmDialog from "@/components/installer/DeleteReportConfirmDialog.vue";
import InstallersSidemenu from "@/components/Sidebar/installer/InstallersSidemenu.vue";
import client from "@/utils/axios";
import { useTranslate } from "@/composables/useTranslate";
import SecondaryPageHeader from "@/components/PageHeader/SecondaryPageHeader.vue";
import { MonthlyBonus, Worker } from "@/types";

const isReportDialogOpen = ref<boolean>(false);
const isDeleteDialogOpen = ref<boolean>(false);
const isEdit = ref<boolean>(false);
const bonusDate = ref<MonthlyBonus | null>(null);
const monthlyBonusList = ref<MonthlyBonus[]>([]);

const workers = ref<Worker[]>([]);
const selectedWorker = ref<string | null>(null);
const selectedMonths = ref<Record<string, string>>({});
const isLoading = ref<boolean>(false);
const month = ref<number | null>(null);
const year = ref<number | null>(null);

const { t } = useTranslate();

const openDialog = () => {
  bonusDate.value = null;
  month.value = null;
  year.value = null;
  isReportDialogOpen.value = true;
  isEdit.value = false;
};

const openDialogEdit = (bonus: MonthlyBonus) => {
  bonusDate.value = bonus;
  month.value = bonus.monthNumber;
  year.value = bonus.year;
  isReportDialogOpen.value = true;
  isEdit.value = true;
};

const openDelete = (bonus: MonthlyBonus) => {
  bonusDate.value = bonus;
  isDeleteDialogOpen.value = true;
};

// Refs

// Functions
const getAllInstallers = async () => {
  isLoading.value = true;
  try {
    const response = await client.get("/installers");
    workers.value = response.data;
  } catch (error) {
    console.log(error);
  } finally {
    isLoading.value = false;
  }
};

const handleSelectWorker = (workerId: string) => {
  if (selectedWorker.value === workerId) {
    selectedWorker.value = null;
  } else {
    selectedWorker.value = workerId;

    if (!selectedMonths.value[workerId]) {
      selectedMonths.value[workerId] = "Current month";
    }
  }
};

const getAllReports = async () => {
  try {
    const response = await client.get("/installers/monthly-bonus/report");

    monthlyBonusList.value = response.data
      .map((item: MonthlyBonus) => ({
        ...item,
        monthNumber: item.month,
        month: t(`months.${item.month}`).value, // convert number to month name
      }))
      .sort((a: any, b: any) => {
        // first sort by year, then by original numeric month
        return a.year - b.year || a.month - b.month;
      });
  } catch (error) {
    console.log(error);
  }
};

const handleSelectMonth = (workerId: string, month: string) => {
  selectedMonths.value[workerId] = month;
};

const closeReport = () => {
  isReportDialogOpen.value = false;
  getAllReports();
};

const closeDelete = () => {
  isDeleteDialogOpen.value = false;
  getAllReports();
};

// Lifecycle hooks
onMounted(() => {
  getAllInstallers();
  getAllReports();
});
</script>

<template>
  <MainLayout>
    <BonusReportDialog
      v-if="isReportDialogOpen"
      :is-report-dialog-open="isReportDialogOpen"
      :is-edit="isEdit"
      :bonus-date="bonusDate"
      :month="String(month)"
      :year="year"
      @on-close="closeReport"
    />
    <DeleteReportConfirmDialog
      :is-delete-dialog-open="isDeleteDialogOpen"
      :bonus-date="bonusDate"
      @on-close="closeDelete"
    />

    <SecondaryPageHeader :is-monthly-bonus="true" />

    <div class="flex w-full h-[calc(100vh-52px)]">
      <InstallersSidemenu
        :workers="workers"
        :selected-worker="selectedWorker"
        :selected-months="selectedMonths"
        @select-worker="handleSelectWorker"
        @select-month="handleSelectMonth"
      />
      <div class="max-w-[934px] pt-[24px] w-full mx-auto h-full">
        <div class="flex h-full flex-col">
          <div class="w-full flex justify-between mb-[12px]">
            <div class="h-[44px] flex items-center justify-center">
              <p class="text-[20px] font-semibold text-[#393840]">
                {{ t("installers.monthly_bonus") }}
              </p>
            </div>
            <button
              @click="openDialog"
              class="flex items-center p-[12px] gap-[8px] text-[16px] text-white font-semibold bg-[#393840] rounded-[7px]"
            >
              <img
                class="h-[18px] w-[18px] text-white"
                src="../../assets/add-plus-circle.svg"
                alt=""
              /><span>{{ t("installers.report_monthly_bonus") }}</span>
            </button>
          </div>
          <div
            class="flex flex-col overflow-y-auto pb-6"
            v-if="monthlyBonusList.length"
          >
            <div
              class="w-full h-[41px] flex items-center justify-start p-[4px] bg-[#E5E4E2] rounded-[7px] mb-[8px]"
            >
              <p class="text-[12px] font-semibold px-[4px] text-[#393840]">
                {{ t("installers.period") }}
              </p>
            </div>

            <div class="flex flex-col h-full gap-2 overflow-y-auto">
              <div
                v-for="(bonus, index) in monthlyBonusList"
                :key="`${bonus?.month}-${bonus.year}-${index}`"
                class="p-[4px] flex justify-between bg-white rounded-[7px]"
              >
                <div class="p-[4px]">
                  <p class="text-[14px] text-[#393840]">
                    {{ bonus.month ? bonus.month?.trim() : "" }}/{{
                      bonus.year
                    }}
                  </p>
                </div>
                <div class="flex items-center justify-end p-[4px]">
                  <img
                    class="w-[16px] h-[16px] cursor-pointer"
                    src="../../assets/edit-pencil-technical.svg"
                    alt=""
                    @click="openDialogEdit(bonus)"
                  />
                  <img
                    class="w-[16px] h-[16px] ml-[10px] cursor-pointer"
                    src="../../assets/trash-icoon.svg"
                    alt=""
                    @click="openDelete(bonus)"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="h-full flex items-center justify-center" v-else>
            <div class="flex flex-col items-center">
              <img
                class="w-[128px] h-[128px] mb-[8px]"
                src="../../assets/checkmark-paper.svg"
                alt=""
              />
              <p class="text-[16px] font-medium text-[#898790]">
                {{ t("installers.no_reports_yet") }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </MainLayout>
</template>
